import React, { ComponentType } from "react";
import styles from "./widget-renderer.module.scss";
import {
  QuerySpec,
  WidgetEncoding,
  WidgetPresentation,
  WidgetSemantics,
  WidgetType,
} from "@src/types/dashboards";
import { CounterNumberWidget } from "./counter-number-gauge/counter-number-widget";
import { SingleTextValueWidget } from "./single-text-value/single-text-value-widget";
import { MultipleTextValuesWidget } from "./multiple-text-values/multiple-text-values-widget";
import { TrendLineWidget } from "./trend-line/trend-line-widget";
import { MultiLineWidget } from "./multi-line/multi-line-widget";
import { ShotDataLineChartWidget } from "./shot-data-line-chart/shot-data-line-chart-widget";
import { MachineOverviewWidget } from "./machine-overview/machine-overview-widget";
import {
  StaticHeaderWidget,
  StaticTextWidget,
  StaticSeparatorWidget,
  StaticLinkWidget,
} from "./tools/static-tool-widgets";

export interface WidgetRendererProps {
  type: WidgetType;
  title: string;
  encoding: WidgetEncoding;
  semantics: WidgetSemantics;
  presentation: WidgetPresentation;
  /** Top-level image URL (canonical location — see `Widget.image_url`).
   *  Renderers read this directly when they want to render an image.
   *  `presentation.image_url` is ignored/deprecated. */
  image_url?: string | null;
  /** Data-source query; when present the widget fetches its live data. */
  query?: QuerySpec | null;
  /** Explicit value override (preview). */
  value?: number;
  /** Preview mode: never fetch live data, render mocked value only. */
  preview?: boolean;
  /** When false (off-canvas widget) the widget's fetches are skipped; the
   *  polling interval keeps its schedule and re-entry refetches once. */
  inViewport?: boolean;
  /** Draw the widget's own title. Off on the canvas (tile header shows it). */
  showTitle?: boolean;
}

/** Each widget component owns its rendering + data fetch (renderer stays generic). */
const WIDGET_COMPONENTS: Partial<
  Record<WidgetType, ComponentType<WidgetRendererProps>>
> = {
  "counter-number": CounterNumberWidget,
  "single-text-value": SingleTextValueWidget,
  "multiple-text-values": MultipleTextValuesWidget,
  "trend-line": TrendLineWidget,
  "multi-line": MultiLineWidget,
  "shot-data-line-chart": ShotDataLineChartWidget,
  "machine-overview": MachineOverviewWidget,
  "static-header": StaticHeaderWidget,
  "static-text": StaticTextWidget,
  "static-separator": StaticSeparatorWidget,
  "static-link": StaticLinkWidget,
};

/**
 * Renders a widget from its template/instance fields. Same component used by the
 * picker preview and the canvas tile. Types without a renderer yet fall back to
 * a neutral tile.
 */
export const WidgetRenderer = ({
  type,
  title,
  ...props
}: WidgetRendererProps) => {
  const Widget = WIDGET_COMPONENTS[type];
  if (!Widget) {
    return (
      <div className={styles["widget-renderer__fallback"]}>
        <span className={styles["widget-renderer__fallback-title"]}>
          {title}
        </span>
        <span className={styles["widget-renderer__fallback-type"]}>{type}</span>
      </div>
    );
  }
  return <Widget type={type} title={title} {...props} />;
};
